import { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, Radio, DatePicker, Popconfirm, message, InputNumber, Dropdown } from 'antd';
import { datePickerLocaleZhCN } from '@core/antdLocales';
import dayjs from 'dayjs';
import './style.less';
import { saveGuestJourney } from '@services/Guest';
import WorkExperiences from '@/components/common/WorkExperiences';
import RemarksTip from '../RemarksTip';
import { logChangeArrivalTransport, logChangeReturnTransport } from '@/utils/conferenceLogger';

const { TextArea } = Input;
const { Option } = Select;

// 交通类型定义
const TransportType = {
    PLANE: 1, // 飞机
    TRAIN: 2, // 火车
};

// 舱等/坐席类型定义
const CabinType = {
    // 飞机舱等
    ECONOMY: '经济舱',
    BUSINESS: '公务舱',
    FIRST: '头等舱',
    // 火车坐席
    SECOND_CLASS: '二等座',
    FIRST_CLASS: '一等座',
    BUSINESS_SEAT: '商务座',
};

// 火车坐席选项
const TRAIN_SEAT_OPTIONS = [
    { value: '二等座', label: '二等座' },
    { value: '一等座', label: '一等座' },
    { value: '商务座', label: '商务座' },
    { value: '软卧', label: '软卧' },
    { value: '硬卧', label: '硬卧' },
    { value: '硬座', label: '硬座' },
];

// 单段行程信息
const createEmptyJourney = () => ({
    id: Date.now().toString(),
    transport_type: '',
    transport_no: '',
    seat_type: '',
    departure_datetime: null,
    departure_place: '',
    arrival_datetime: null,
    arrival_place: '',
});

// 表单值类型
const JourneyEditModal = ({ type, open, initialData, guestInfo, conferenceId, expertId, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [journeys, setJourneys] = useState([]);

    // 标题文本
    const titleText = type === 'arrival' ? '变更来程交通' : '变更返程交通';

    // 初始化表单数据
    useEffect(() => {
        if (open) {
            if (initialData) {
                // 回显原行程
                // need字段：1-会务组安排, 2-自费, 0-不需要
                let needValue = null; // 默认会务组安排
                if (initialData.need === false || initialData.need === 0) {
                    needValue = 0; // 不需要
                }
                if (initialData.need === 2) {
                    needValue = 2; // 自费
                }
                if (initialData.need === 1) {
                    needValue = 1; // 会务组安排
                }

                const formValues = {
                    need: needValue,
                    updated_reason: '', // 变更理由不回显
                    remarks: initialData.remarks || '',
                };

                // 回显交通信息
                const initialJourneys =
                    initialData.transport_list && initialData.transport_list.length > 0
                        ? initialData.transport_list.map((transport, index) => ({
                              id: `${Date.now()}_${index}`,
                              transport_type: transport.transport_type,
                              transport_no: transport.transport_no,
                              seat_type: transport.seat_type,
                              departure_datetime: transport.departure_datetime ? dayjs(transport.departure_datetime) : null,
                              departure_place: transport.departure_place,
                              arrival_datetime: transport.arrival_datetime ? dayjs(transport.arrival_datetime) : null,
                              arrival_place: transport.arrival_place,
                          }))
                        : [createEmptyJourney()];

                setJourneys(initialJourneys);

                // 设置交通信息表单字段的初始值
                form.setFieldsValue({
                    ...formValues,
                    journeys: initialJourneys,
                });
            } else {
                // 新增时，不设置默认值
                const defaultValues = {
                    need: null, // 不设置默认值
                    updated_reason: '',
                    remarks: '',
                };

                const emptyJourney = createEmptyJourney();
                setJourneys([emptyJourney]);

                // 设置初始交通信息表单字段的值
                form.setFieldsValue({
                    ...defaultValues,
                    journeys: [emptyJourney],
                });
                form.resetFields();
            }
        }
    }, [open, initialData, form, type]);

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        setJourneys([]);
        onCancel();
    };

    // 处理提交
    const handleSubmit = async () => {
        try {
            // 验证表单
            const values = await form.validateFields();

            let formValues;

            // 交通信息提交
            // 如果选择不需要交通，则只传递 need 标志
            if (values.need === 0) {
                formValues = {
                    ...initialData, // 保留原交通信息
                    need: 0,
                    updated_reason: values.updated_reason,
                    remarks: values.remarks,
                };
            } else {
                // 从表单值中提取交通信息
                const transportList =
                    values.journeys && values.journeys.length
                        ? values.journeys.map(journey => ({
                              transport_type: journey.transport_type,
                              transport_no: journey.transport_no,
                              seat_type: journey.seat_type,
                              departure_datetime: journey.departure_datetime ? journey.departure_datetime.format('YYYY-MM-DD HH:mm') : '',
                              arrival_datetime: journey.arrival_datetime ? journey.arrival_datetime.format('YYYY-MM-DD HH:mm') : '',
                              departure_place: journey.departure_place,
                              arrival_place: journey.arrival_place,
                              ticket_ready: false, // 默认未出票，后续根据逻辑修改
                          }))
                        : [];

                // 按照 departure_datetime 从早到晚排序
                // 没有出发时间的排在最后
                transportList.sort((a, b) => {
                    // 如果都有出发时间，按时间排序
                    if (a.departure_datetime && b.departure_datetime) {
                        return a.departure_datetime.localeCompare(b.departure_datetime);
                    }
                    // 如果a有时间，b没有，a排前面
                    if (a.departure_datetime && !b.departure_datetime) {
                        return -1;
                    }
                    // 如果b有时间，a没有，b排前面
                    if (!a.departure_datetime && b.departure_datetime) {
                        return 1;
                    }
                    // 如果都没有时间，保持原顺序
                    return 0;
                });

                // 获取编辑前的交通列表（用于判断已读未读和出票状态）
                const originalTransportList = initialData?.transport_list || [];

                // 一、判断已读未读状态
                let hasRead = initialData?.has_read !== false; // 默认保持原有状态，如果原来是false则保持false

                // 1. 判断数组长度是否变化
                if (originalTransportList.length !== transportList.length) {
                    hasRead = false;
                } else {
                    // 2. 数组长度没变，判断每一条编辑前的数据是否在编辑后的列表中存在
                    for (const originalTransport of originalTransportList) {
                        const exists = transportList.some(newTransport => newTransport.transport_type === originalTransport.transport_type && newTransport.transport_no === originalTransport.transport_no);

                        // 如果有一条原始数据在新列表中不存在，则标记为未读
                        if (!exists) {
                            hasRead = false;
                            break;
                        }
                    }
                }

                // 二、判断单个交通数据的出票状态
                transportList.forEach(newTransport => {
                    // 根据 transport_type、transport_no、seat_type 三个字段在编辑前的列表中查找
                    const matchedOriginal = originalTransportList.find(originalTransport => originalTransport.transport_type === newTransport.transport_type && originalTransport.transport_no === newTransport.transport_no && originalTransport.seat_type === newTransport.seat_type);

                    // 如果找到匹配的数据，保持原有的 ticket_ready 状态
                    if (matchedOriginal) {
                        newTransport.ticket_ready = matchedOriginal.ticket_ready || false;
                    } else {
                        // 否则设置为 false
                        newTransport.ticket_ready = false;
                    }
                });

                // 三、计算总出票状态
                // 0: 未出票（全部false）, 1: 全部出票（全部true）, 2: 部分出票（有true有false）
                const issuedCount = transportList.filter(t => t.ticket_ready === true).length;
                const totalCount = transportList.length;

                let totalTicketReady;
                if (issuedCount === 0) {
                    totalTicketReady = 0; // 全部未出票
                } else if (issuedCount === totalCount) {
                    totalTicketReady = 1; // 全部出票
                } else {
                    totalTicketReady = 2; // 部分出票
                }

                formValues = {
                    need: values.need, // 1: 会务组安排, 2: 自费
                    updated_reason: values.updated_reason,
                    remarks: values.remarks,
                    transport_list: transportList,
                    has_read: hasRead, // 添加已读未读状态
                    total_ticket_ready: totalTicketReady, // 添加总出票状态
                };
            }

            // 调用保存接口
            const params = {
                conferenceId,
                expertId,
            };

            // 添加更新时间字段
            const dataWithTimestamp = {
                ...formValues,
                updated_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };

            // 根据类型设置不同的字段
            if (type === 'arrival') {
                params.arrivalTransport = JSON.stringify(dataWithTimestamp);
            } else {
                params.returnTransport = JSON.stringify(dataWithTimestamp);
            }

            const res = await saveGuestJourney(params);

            if (res.status_code === 200) {
                message.success('保存成功');
                form.resetFields();
                setJourneys([]);
                onSubmit && onSubmit(formValues);

                // 上报操作日志
                // 只有在变更交通信息时才上报（存在 initialData 且变更了交通信息）
                if (initialData && formValues.transport_list && formValues.transport_list.length > 0) {
                    try {
                        // 格式化交通信息数组
                        const transports = formValues.transport_list.map(transport => ({
                            transport_type: transport.transport_type,
                            transport_type_text: transport.transport_type === TransportType.PLANE ? '飞机' : transport.transport_type === TransportType.TRAIN ? '火车' : '',
                            transport_no: transport.transport_no || '',
                            seat_type: transport.seat_type || '',
                            departure_datetime: transport.departure_datetime || '',
                            arrival_datetime: transport.arrival_datetime || '',
                            departure_place: transport.departure_place || '',
                            arrival_place: transport.arrival_place || '',
                        }));

                        const logOptions = {
                            conferenceId,
                            expertId,
                            expertName: guestInfo?.name || '',
                            transports,
                            reason: formValues.updated_reason || '',
                            isSuccess: true,
                        };

                        // 根据类型调用不同的日志函数
                        if (type === 'arrival') {
                            await logChangeArrivalTransport(logOptions);
                        } else {
                            await logChangeReturnTransport(logOptions);
                        }
                    } catch (logError) {
                        console.error('操作日志上报失败:', logError);
                        // 日志上报失败不影响主流程
                    }
                }
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.log('保存失败:', error);
            message.error('保存失败');
        }
    };

    // 更新行程字段
    const updateJourney = (name, field, value) => {
        // 更新内部状态
        setJourneys(prev =>
            prev.map((journey, index) => {
                if (index === name) {
                    const updated = { ...journey, [field]: value };
                    // 当交通方式改变时，自动调整舱等/坐席默认值
                    if (field === 'transport_type') {
                        if (value === TransportType.PLANE) {
                            updated.seat_type = CabinType.ECONOMY;
                        } else if (value === TransportType.TRAIN) {
                            updated.seat_type = CabinType.SECOND_CLASS;
                        }
                    }
                    return updated;
                }
                return journey;
            }),
        );

        // 更新表单字段
        const currentJourneys = form.getFieldValue('journeys') || [];
        const updatedJourneys = currentJourneys.map((journey, index) => {
            if (index === name) {
                const updated = { ...journey, [field]: value };
                // 当交通方式改变时，自动调整舱等/坐席默认值
                if (field === 'transport_type') {
                    if (value === TransportType.PLANE) {
                        updated.seat_type = CabinType.ECONOMY;
                    } else if (value === TransportType.TRAIN) {
                        updated.seat_type = CabinType.SECOND_CLASS;
                    }
                }
                return updated;
            }
            return journey;
        });

        form.setFieldsValue({ journeys: updatedJourneys });
    };

    // 清空行程
    const handleClearJourney = name => {
        // 获取当前表单值
        const currentJourneys = form.getFieldValue('journeys') || [];

        // 更新表单值
        form.setFieldsValue({
            journeys: currentJourneys.map((journey, index) => {
                if (index === name) {
                    // 创建一个新对象，保留必要的字段，清空指定字段
                    return {
                        ...journey,
                        transport_type: '',
                        transport_no: '',
                        seat_type: '',
                        departure_datetime: null,
                        departure_place: '',
                        arrival_datetime: null,
                        arrival_place: '',
                    };
                }
                return journey;
            }),
        });

        // 更新内部状态
        setJourneys(prev =>
            prev.map((journey, index) => {
                if (index === name) {
                    return {
                        ...journey,
                        transport_type: '',
                        transport_no: '',
                        seat_type: '',
                        departure_datetime: null,
                        departure_place: '',
                        arrival_datetime: null,
                        arrival_place: '',
                    };
                }
                return journey;
            }),
        );
    };

    // 移除行程
    const handleRemoveJourney = (name, remove) => {
        // 调用Form.List的remove方法
        remove(name);

        // 更新内部状态
        setTimeout(() => {
            const currentJourneys = form.getFieldValue('journeys') || [];
            setJourneys(currentJourneys);
        }, 0);
    };

    // 新增行程
    const handleAddJourney = () => {
        const newJourney = createEmptyJourney();
        setJourneys(prev => [...prev, newJourney]);
    };

    // 处理火车坐席选择
    const handleTrainSeatSelect = (name, value) => {
        // 更新表单字段
        const currentJourneys = form.getFieldValue('journeys') || [];
        const updatedJourneys = currentJourneys.map((journey, index) => {
            if (index === name) {
                return { ...journey, seat_type: value };
            }
            return journey;
        });

        form.setFieldsValue({ journeys: updatedJourneys });

        // 同时更新内部状态
        setJourneys(prev =>
            prev.map((journey, index) => {
                if (index === name) {
                    return { ...journey, seat_type: value };
                }
                return journey;
            }),
        );
    };

    return (
        <Modal
            title={titleText}
            open={open}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取消
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit}>
                    提交
                </Button>,
            ]}
            width={600}
            className="journey-edit-modal"
            style={{ maxHeight: '80vh', overflow: 'hidden' }}
            styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' } }}
        >
            {/* 嘉宾基本信息 */}
            {guestInfo && (
                <div className="guest-basic-info">
                    <div className="info-item">
                        <span className="label">姓名：</span>
                        <span className="value">
                            {guestInfo.name || '–'}
                            {(guestInfo.gender === 0 || guestInfo.gender === 1) && <i className={`bi bi-person-fill gender-icon ${guestInfo.gender === 0 ? 'female' : 'male'}`}></i>}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">级别：</span>
                        <span className="value line-level">{guestInfo.levelLabel || '–'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">单位：</span>
                        <span className="value">
                            <WorkExperiences workExperiences={guestInfo.workExperiences} layout="inline" showSerialNumber={false} emptyText="–" />
                        </span>
                    </div>
                </div>
            )}

            <div className="modal-content">
                {/* 系统建议时间 */}
                {guestInfo?.suggestedTime && (
                    <div className="suggested-date-section">
                        <span className="system-tag">系统</span>
                        <span className="date-text">
                            {type === 'arrival' ? '已接受日程最早开始时间' : '已接受日程最晚结束时间'} {guestInfo.suggestedTime}
                        </span>
                    </div>
                )}

                <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                    {/* 来程或返程交通需求 */}
                    <Form.Item label={`${type === 'arrival' ? '来程' : '返程'}交通需求`} name="need">
                        <Radio.Group>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* 变更理由 - 仅当交通卡片按钮为"变更"时显示（即存在交通信息时） */}
                    {initialData && initialData.transport_list && initialData.transport_list.length > 0 && (
                        <Form.Item label="变更理由" name="updated_reason" rules={[{ required: true, message: '请填写变更理由' }]}>
                            <TextArea placeholder="填写变更理由" maxLength={100} showCount rows={3} />
                        </Form.Item>
                    )}

                    {/* 仅当选择"会务组安排"或"自费"时显示交通详情表单 */}
                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.need !== currentValues.need}>
                        {({ getFieldValue }) => {
                            const transportNeeded = getFieldValue('need');
                            return !!transportNeeded ? (
                                <>
                                    <Form.List name="journeys">
                                        {(fields, { add, remove }) => (
                                            <>
                                                {fields.map(({ key, name, ...restField }) => {
                                                    const journey = journeys[name] || {};
                                                    return (
                                                        <div key={key} className="journey-segment">
                                                            {/* 操作按钮 */}
                                                            <div className="segment-actions">
                                                                {fields.length > 1 && (
                                                                    <Popconfirm title="确认移除行程信息？" onConfirm={() => handleRemoveJourney(name, remove)} okText="确认" cancelText="取消" placement="topRight">
                                                                        <span className="action-link">移除</span>
                                                                    </Popconfirm>
                                                                )}
                                                            </div>

                                                            {/* 交通方式 */}
                                                            <Form.Item {...restField} label="交通方式" name={[name, 'transport_type']} initialValue={journey.transport_type}>
                                                                <Radio.Group onChange={e => updateJourney(name, 'transport_type', e.target.value)}>
                                                                    <Radio value={TransportType.PLANE}>飞机</Radio>
                                                                    <Radio value={TransportType.TRAIN}>火车</Radio>
                                                                </Radio.Group>
                                                            </Form.Item>

                                                            {/* 航班号/车次号 */}
                                                            <Form.Item {...restField} label={form.getFieldValue(['journeys', name, 'transport_type']) === TransportType.PLANE ? '航班号' : '车次'} name={[name, 'transport_no']} initialValue={journey.transport_no} rules={[{ required: true, message: '请填写航班号/车次' }]}>
                                                                <Input placeholder={form.getFieldValue(['journeys', name, 'transport_type']) === TransportType.PLANE ? '填写飞机航班号' : '填写车次'} onChange={e => updateJourney(name, 'transport_no', e.target.value)} />
                                                            </Form.Item>

                                                            {/* 舱等/坐席 */}
                                                            <Form.Item {...restField} label={form.getFieldValue(['journeys', name, 'transport_type']) === TransportType.PLANE ? '舱等' : '坐席'} name={[name, 'seat_type']} initialValue={journey.seat_type}>
                                                                {form.getFieldValue(['journeys', name, 'transport_type']) === TransportType.PLANE ? (
                                                                    <Radio.Group onChange={e => updateJourney(name, 'seat_type', e.target.value)}>
                                                                        <Radio value={CabinType.ECONOMY}>经济舱</Radio>
                                                                        <Radio value={CabinType.BUSINESS}>公务舱</Radio>
                                                                        <Radio value={CabinType.FIRST}>头等舱</Radio>
                                                                    </Radio.Group>
                                                                ) : (
                                                                    // 火车坐席输入框带下拉选择
                                                                    <div className="input-with-select">
                                                                        <Form.Item {...restField} name={[name, 'seat_type']} noStyle>
                                                                            <Input
                                                                                placeholder="填写坐席"
                                                                                style={{ width: '100%' }}
                                                                                suffix={
                                                                                    <div
                                                                                        className="input-addon"
                                                                                        onClick={e => {
                                                                                            // 阻止事件冒泡
                                                                                            e.stopPropagation();
                                                                                            // 触发下拉菜单
                                                                                            const dropdownElement = document.querySelector(`.dropdown-trigger-${name}`);
                                                                                            if (dropdownElement) {
                                                                                                dropdownElement.click();
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <Button type="link">选择坐席</Button>
                                                                                        <i className="bi bi-chevron-down addon-icon"></i>
                                                                                    </div>
                                                                                }
                                                                                value={journey.seat_type || ''}
                                                                                onChange={e => updateJourney(name, 'seat_type', e.target.value)}
                                                                            />
                                                                        </Form.Item>
                                                                        {/* 下拉菜单 */}
                                                                        <Dropdown
                                                                            trigger={['click']}
                                                                            menu={{
                                                                                items: TRAIN_SEAT_OPTIONS.map(option => ({
                                                                                    key: option.value,
                                                                                    label: option.label,
                                                                                    onClick: () => handleTrainSeatSelect(name, option.value),
                                                                                })),
                                                                            }}
                                                                        >
                                                                            <div className={`dropdown-overlay dropdown-trigger-${name}`} />
                                                                        </Dropdown>
                                                                    </div>
                                                                )}
                                                            </Form.Item>

                                                            {/* 出发时间 */}
                                                            <Form.Item {...restField} label="出发时间" name={[name, 'departure_datetime']} initialValue={journey.departure_datetime ? dayjs(journey.departure_datetime) : null}>
                                                                <DatePicker showTime placeholder="填写出发时间" onChange={value => updateJourney(name, 'departure_datetime', value)} format="YYYY-MM-DD HH:mm" locale={datePickerLocaleZhCN} style={{ width: '100%' }} />
                                                            </Form.Item>

                                                            {/* 出发地点 */}
                                                            <Form.Item {...restField} label="出发地点" name={[name, 'departure_place']} initialValue={journey.departure_place}>
                                                                <Input placeholder="填写出发地点" onChange={e => updateJourney(name, 'departure_place', e.target.value)} />
                                                            </Form.Item>

                                                            {/* 抵达时间 */}
                                                            <Form.Item {...restField} label="抵达时间" name={[name, 'arrival_datetime']} initialValue={journey.arrival_datetime ? dayjs(journey.arrival_datetime) : null}>
                                                                <DatePicker showTime placeholder="填写抵达时间" onChange={value => updateJourney(name, 'arrival_datetime', value)} format="YYYY-MM-DD HH:mm" locale={datePickerLocaleZhCN} style={{ width: '100%' }} />
                                                            </Form.Item>

                                                            {/* 抵达地点 */}
                                                            <Form.Item {...restField} label="抵达地点" name={[name, 'arrival_place']} initialValue={journey.arrival_place}>
                                                                <Input placeholder="填写抵达地点" onChange={e => updateJourney(name, 'arrival_place', e.target.value)} />
                                                            </Form.Item>
                                                        </div>
                                                    );
                                                })}

                                                {/* 新增行程按钮 */}
                                                <div
                                                    className="add-journey-btn"
                                                    onClick={() => {
                                                        const newJourney = createEmptyJourney();
                                                        add(newJourney);
                                                        // 更新内部状态以确保与表单同步
                                                        setTimeout(() => {
                                                            const currentJourneys = form.getFieldValue('journeys') || [];
                                                            setJourneys(currentJourneys);
                                                        }, 0);
                                                    }}
                                                >
                                                    <i className="bi bi-plus"></i>
                                                    <span>新增行程</span>
                                                </div>
                                            </>
                                        )}
                                    </Form.List>
                                </>
                            ) : null;
                        }}
                    </Form.Item>

                    {/* 备注 - 移到新增行程按钮下方 */}
                    <div className="remarks-section">
                        <Form.Item label={type === 'arrival' ? '来程备注' : type === 'return' ? '返程备注' : '住宿备注'} style={{ marginBottom: 0 }}>
                            <Form.Item name="remarks" noStyle>
                                <TextArea placeholder={`填写${type === 'arrival' ? '来程' : type === 'return' ? '返程' : '住宿'}备注信息`} rows={3} />
                            </Form.Item>
                            <RemarksTip />
                        </Form.Item>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default JourneyEditModal;
